import type { PlanktonState } from './types';
import { REGIONS, type RegionId } from './data/oceans';
import { REGION_EXCHANGE } from './data/currents/regionExchange';
import { held } from './held';

/**
 * The plankton carried between the regions by the currents (M66): each day,
 * the mixed-layer water of every pair of regions the drifters move between
 * is exchanged, and the nutrients, the plankton and the detritus with it.
 *
 * **The rates** are NOAA's drifters' (`scripts/data/drifters.ts`): a region's
 * rate to another, d⁻¹, the moves of drogued drifters from it to the other a
 * month on over the days they spent in it, 1979 to 2025 (MEASURED). A drifter
 * stays with the water at the top of the mixed layer (its drogue at 15 m),
 * which is the water the plankton lives in.
 *
 * **The exchange** is two-way and keeps each region's water. The two
 * directions do not move the same water: drifters gather where the surface
 * water sinks and leave where it rises (the Southern Ocean's surface reaches
 * the South Atlantic's high band at 55 times what comes back, each
 * direction's area times its rate, MEASURED from the table). A whole account
 * would carry each direction's own water, the smaller flow mixing the two
 * sideways and the rest returned through the floors, sinking where the
 * surface gathers and rising, with the water below, where it leaves; the
 * slab model has no such return (open, review M69). Each pair exchanges
 * instead the mean of the water each direction's rate moves (SET BY HAND: a
 * choice between the sideways mixing alone, the smaller flow, and the whole
 * transport, the larger, set against nothing measured; the regions'
 * mixings, fitted with it in place, take up what it leaves):
 *
 *   F = (A_a h_a r_ab + A_b h_b r_ba) / 2, km² m a day (only F over each side's
 *   water, A h in the same units, is used),
 *
 * A a region's area (the Atlas's surface cells), h its mixed layer that day,
 * and each region's tracers move toward the other's by F over its own water,
 * A h: what one gains the other loses. Every pair's exchange is reckoned
 * from the day's states before any is applied, so no region's order matters.
 *
 * **Not the aquarium's**: its column is the ocean around Maui Nui already (the
 * Atlas's cells there, its mixing fitted to Kahe Point's measurements, M60),
 * and the water the currents bring through its square is that ocean's; a
 * region's mean, the whole band from the equator to 40°N for Maui Nui, is
 * not (`docs/KNOWN_ISSUES.md`, M66). Drogued drifters stay in its square a
 * median of a day or two (`aquariumResidence`, MEASURED).
 */

/** A day's move of each tracer the water carries, in a region, mmol m⁻³. */
type Moved = Record<'phosphate' | 'nitrate' | 'phyto' | 'zoo' | 'detritus' | 'fixers', number>;

/** The regions' rates, d⁻¹, and their areas, km² (`scripts/data/drifters.ts`). */
export interface ExchangeTable {
  ratesPerDay: Readonly<Record<RegionId, Readonly<Partial<Record<RegionId, number>>>>>;
  areasKm2: Readonly<Record<RegionId, number>>;
}

/** Every pair the drifters move between, each once, in the regions' order, with its two rates, d⁻¹. */
export function exchangePairs(): Array<{ a: RegionId; b: RegionId; ab: number; ba: number }> {
  const pairs: Array<{ a: RegionId; b: RegionId; ab: number; ba: number }> = [];
  REGIONS.forEach((ra, i) => {
    for (const rb of REGIONS.slice(i + 1)) {
      const ab = REGION_EXCHANGE.ratesPerDay[ra.id][rb.id] ?? 0;
      const ba = REGION_EXCHANGE.ratesPerDay[rb.id][ra.id] ?? 0;
      if (ab > 0 || ba > 0) pairs.push({ a: ra.id, b: rb.id, ab, ba });
    }
  });
  return pairs;
}

/** Every pair, its regions by their places in the regions' order, with their areas, km² (read once: the table is code). */
const PAIRS = exchangePairs().map(({ a, b, ab, ba }) => ({
  i: REGIONS.findIndex((r) => r.id === a),
  j: REGIONS.findIndex((r) => r.id === b),
  ab,
  ba,
  areaA: REGION_EXCHANGE.areasKm2[a],
  areaB: REGION_EXCHANGE.areasKm2[b],
}));

/** The regions' plankton a day on by the currents (above), each held to the game's decimals. */
export function exchangePlankton(states: Readonly<Record<RegionId, PlanktonState>>): Record<RegionId, PlanktonState> {
  const before = REGIONS.map((r) => states[r.id]);
  const delta = REGIONS.map((): Moved => ({ phosphate: 0, nitrate: 0, phyto: 0, zoo: 0, detritus: 0, fixers: 0 }));
  for (const { i, j, ab, ba, areaA, areaB } of PAIRS) {
    const sa = before[i] as PlanktonState;
    const sb = before[j] as PlanktonState;
    const va = areaA * sa.mixedLayerM;
    const vb = areaB * sb.mixedLayerM;
    const f = (va * ab + vb * ba) / 2;
    moveToward(delta[i] as Moved, delta[j] as Moved, sa, sb, f / va, f / vb);
  }
  const after = {} as Record<RegionId, PlanktonState>;
  REGIONS.forEach((r, i) => { after[r.id] = withExchange(before[i] as PlanktonState, delta[i] as Moved); });
  return after;
}

/**
 * A pair's day added to its sides' moves (above): each side's tracers toward
 * the other's, by its share, F over its own water. Each tracer by name, as
 * `withExchange` writes them (review M69: with each read through a list of
 * their names, the exchange took two fifths of the tick's time, MEASURED
 * stage by stage).
 */
export function moveToward(da: Moved, db: Moved, sa: PlanktonState, sb: PlanktonState, shareA: number, shareB: number): void {
  let d = sb.phosphate - sa.phosphate; da.phosphate += shareA * d; db.phosphate -= shareB * d;
  d = sb.nitrate - sa.nitrate; da.nitrate += shareA * d; db.nitrate -= shareB * d;
  d = sb.phyto - sa.phyto; da.phyto += shareA * d; db.phyto -= shareB * d;
  d = sb.zoo - sa.zoo; da.zoo += shareA * d; db.zoo -= shareB * d;
  d = sb.detritus - sa.detritus; da.detritus += shareA * d; db.detritus -= shareB * d;
  d = sb.fixers - sa.fixers; da.fixers += shareA * d; db.fixers -= shareB * d;
}

/** A region's plankton with its day's exchange added to each tracer, mmol m⁻³, held to the game's decimals. */
export function withExchange(s: PlanktonState, d: Readonly<Moved>): PlanktonState {
  return { ...s, phosphate: held(s.phosphate + d.phosphate), nitrate: held(s.nitrate + d.nitrate), phyto: held(s.phyto + d.phyto), zoo: held(s.zoo + d.zoo), detritus: held(s.detritus + d.detritus), fixers: held(s.fixers + d.fixers) };
}
